import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { PageSpinner } from '../../../components/PageSpinner';
import { catalogueApi } from '../../../features/catalogue/catalogue.api';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { paths } from '../../../routes/paths';
import styles from '../AppPages.module.css';

export function ProductDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();

  const product = useQuery({
    queryKey: ['catalogue', 'products', id],
    queryFn: () => catalogueApi.getPublicProduct(id),
  });

  return (
    <div className={styles.page}>
      {product.isPending && <PageSpinner label="Loading product" />}
      {product.isError && <Alert tone="error">{toDisplayMessage(product.error)}</Alert>}

      {product.isSuccess && (
        <>
          <header className={styles.pageHeader}>
            <h1 className={styles.pageTitle}>{product.data.name}</h1>
            <Link to={`${paths.customerApply}?productId=${encodeURIComponent(product.data.id)}`}>
              <Button>Start application</Button>
            </Link>
          </header>

          <section className={styles.card} aria-labelledby="product-details-heading">
            <h2 className={styles.cardTitle} id="product-details-heading">
              Details
            </h2>

            <dl className={styles.definitionList}>
              <dt>Code</dt>
              <dd>{product.data.code}</dd>
              <dt>Capacity</dt>
              <dd>{product.data.systemCapacityKw} kW</dd>
              <dt>Price</dt>
              <dd>{formatNgn(product.data.basePrice)}</dd>
              <dt>Category</dt>
              <dd>{product.data.category}</dd>
              {product.data.warrantySummary !== null && (
                <>
                  <dt>Warranty</dt>
                  <dd>{product.data.warrantySummary}</dd>
                </>
              )}
            </dl>

            <p>{product.data.description}</p>
          </section>

          {product.data.components !== undefined && product.data.components.length > 0 && (
            <section className={styles.card} aria-labelledby="components-heading">
              <h2 className={styles.cardTitle} id="components-heading">
                Components
              </h2>
              <ul>
                {product.data.components.map((component) => (
                  <li key={component.id}>
                    {component.componentName} × {component.quantity} {component.unit}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <p>
            <Link to={paths.customerPlans}>View repayment plans</Link>
          </p>
        </>
      )}
    </div>
  );
}
